import { AuditOutlined, MessageOutlined } from '@ant-design/icons'
import FormCard from '@components/FormCard'
import { useHasAccess } from '@components/HasAccess'
import Page from '@components/Page/Page'
import { NotificationContentKey } from '@models/NotificationContent'
import { SettingKey } from '@models/Setting'
import { useTenant } from '@modules/tenant/contexts/tenant/TenantContext'
import { useAppDispatch } from '@store/hooks'
import { getNotificationContents } from '@store/NotificationContentReducer'
import { getSettings } from '@store/SettingReducer'
import { PageTitle } from '@supplement-bacon/react-toolkit/components'
import { SBRMAction } from '@supplement-bacon/sbrm-react'
import { Entity } from '@utils/Entities'
import MetadataKey from '@utils/MetadataKey'
import { Permission } from '@utils/permissions'
import { Col, Divider, Flex, Row, Tag, Typography } from 'antd'
import { useEffect } from 'react'

const { Text } = Typography

const MobileAppsPage = () => {
  const dispatch = useAppDispatch()
  const { hasAccess } = useHasAccess()
  const { allPlatforms, platforms } = useTenant()

  useEffect(() => {
    if (hasAccess(Permission.editSettingsPlatform)) {
      dispatch(
        getSettings([
          SettingKey.APP_MINIMUM_VERSION_IOS,
          SettingKey.APP_MINIMUM_VERSION_ANDROID,
        ])
      )
      dispatch(
        getNotificationContents([
          NotificationContentKey.REGISTER_REQUIRED_OPT_IN_DATA,
          NotificationContentKey.REGISTER_OPTIONAL_OPT_IN_MARKETING,
          NotificationContentKey.REGISTER_EXTRA_TEXT,
        ])
      )
    }
    if (hasAccess(Permission.manageVisibilityChat)) {
      dispatch(getSettings([SettingKey.CHAT_VISIBILITY]))
    }
  }, [])

  return (
    <Page>
      <Flex vertical gap={100}>
        <Flex vertical gap={0}>
          <PageTitle
            level={4}
            title={'Mobile applications'}
            style={{ margin: 0 }}
          />
          <Divider />
          <Row gutter={[32, 32]}>
            <Col span={12}>
              <FormCard
                disabled={
                  platforms['ios']?.identifier === undefined ||
                  !hasAccess(Permission.editSettingsPlatform)
                }
                title="iOS minimum version"
                description="Apps below this version will be prompted on opening a non-closable popup forcing to update the app with a button pointing to the store"
                icon={allPlatforms['ios'].icon}
                entity={Entity.setting}
                action={SBRMAction.Update}
                metadata={[
                  {
                    key: MetadataKey.Setting,
                    value: SettingKey.APP_MINIMUM_VERSION_IOS,
                  },
                ]}
              />
            </Col>
            <Col span={12}>
              <FormCard
                disabled={
                  platforms['android']?.identifier === undefined ||
                  !hasAccess(Permission.editSettingsPlatform)
                }
                title="Android minimum version"
                description="Apps below this version will be prompted on opening a non-closable popup forcing to update the app with a button pointing to the store"
                icon={allPlatforms['android'].icon}
                entity={Entity.setting}
                action={SBRMAction.Update}
                metadata={[
                  {
                    key: MetadataKey.Setting,
                    value: SettingKey.APP_MINIMUM_VERSION_ANDROID,
                  },
                ]}
              />
            </Col>
            <Col span={12}>
              <FormCard
                title="Chat visibility across all mobile applications"
                icon={<MessageOutlined />}
                entity={Entity.setting}
                action={SBRMAction.Update}
                disabled={!hasAccess(Permission.manageVisibilityChat)}
                metadata={[
                  {
                    key: MetadataKey.Setting,
                    value: SettingKey.CHAT_VISIBILITY,
                  },
                ]}
              />
            </Col>
          </Row>
        </Flex>
        <Flex vertical gap={0}>
          <PageTitle
            level={4}
            title={'Registration - Legal texts'}
            style={{ margin: 0, marginTop: 50 }}
            toolbar={
              <Tag>
                <Text italic type="secondary">
                  Since app v3.12.0
                </Text>
              </Tag>
            }
          />
          <Divider />

          <Row gutter={[32, 32]}>
            <Col span={12}>
              <FormCard
                title="Data privacy opt-in"
                icon={<AuditOutlined />}
                entity={Entity.notificationContent}
                action={SBRMAction.Update}
                description="This is the mandatory opt-in that must to be ticked to complete the registration"
                disabled={!hasAccess(Permission.manageVisibilityChat)}
                metadata={[
                  {
                    key: MetadataKey.NotificationContent,
                    value: NotificationContentKey.REGISTER_REQUIRED_OPT_IN_DATA,
                  },
                ]}
              />
            </Col>
            <Col span={12}>
              <FormCard
                title="Marketing opt-in"
                icon={<AuditOutlined />}
                entity={Entity.notificationContent}
                action={SBRMAction.Update}
                description="This is the optional opt-in that might not be ticked during the registration"
                disabled={!hasAccess(Permission.manageVisibilityChat)}
                metadata={[
                  {
                    key: MetadataKey.NotificationContent,
                    value:
                      NotificationContentKey.REGISTER_OPTIONAL_OPT_IN_MARKETING,
                  },
                ]}
              />
            </Col>
            <Col span={12}>
              <FormCard
                title="Extra Text"
                icon={<AuditOutlined />}
                entity={Entity.notificationContent}
                action={SBRMAction.Update}
                disabled={!hasAccess(Permission.manageVisibilityChat)}
                description="This is an optional text displayed above the submit registration button"
                metadata={[
                  {
                    key: MetadataKey.NotificationContent,
                    value: NotificationContentKey.REGISTER_EXTRA_TEXT,
                  },
                ]}
              />
            </Col>
          </Row>
        </Flex>
      </Flex>
    </Page>
  )
}

export default MobileAppsPage
